import { unzipSync } from 'fflate';
import { validateBackup } from './project.js';

const MAX_BYTES = 32 * 1024 * 1024;
const MAX_NOTES = 100000;
const MAX_TIME = 8 * 60 * 60;
const finite = n => typeof n === 'number' && Number.isFinite(n);
const views = () => ({ falling: true, keyboard: true, chords: true, sheet: false });
const uid = () => crypto.randomUUID();
const fail = message => { throw new Error(message); };

function metadataCopy(value, depth = 0, budget = { remaining: 4096 }) {
  if (--budget.remaining < 0 || depth > 6) fail('Extraction metadata is too large.');
  if (value === null || typeof value === 'boolean') return value;
  if (typeof value === 'string' && value.length <= 500) return value;
  if (finite(value) && Math.abs(value) <= 1e9) return value;
  if (Array.isArray(value) && value.length <= 256) return value.map(v => metadataCopy(v, depth + 1, budget));
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype && Object.keys(value).length <= 256) {
    return Object.fromEntries(Object.entries(value).map(([key, v]) => {
      if (key.length > 100 || ['__proto__', 'constructor', 'prototype'].includes(key)) fail('Invalid extraction metadata key.');
      return [key, metadataCopy(v, depth + 1, budget)];
    }));
  }
  fail('Invalid extraction metadata.');
}

function baseMusic(name, kind, notes, tracks, tempos, warnings = [], measures = []) {
  if (!notes.length) fail('This file contains no pitched notes.');
  const music = {
    version: 1, id: uid(), title: name.replace(/\.[^.]+$/, ''),
    source: { name, kind, confidence: 1, reviewed: true }, notes, tracks, tempos, measures,
    duration: notes.reduce((end, n) => Math.max(end, n.start + n.duration), 0),
    anchors: [], views: views(), warnings,
  };
  return validateMusic(music);
}

/** Validate and copy all imported data before it reaches IndexedDB or the views. */
export function validateMusic(input) {
  if (!input || input.version !== 1 || !input.source || !['midi', 'musicxml', 'audio', 'visual'].includes(input.source.kind)) fail('Unsupported music data.');
  if (typeof input.source.name !== 'string' || input.source.name.length > 500) fail('Invalid music source name.');
  if (!Array.isArray(input.notes) || input.notes.length > MAX_NOTES) fail('Music must contain at most 100,000 notes.');
  const ids = new Set();
  const notes = input.notes.map((n, index) => {
    if (!n || !Number.isInteger(n.pitch) || n.pitch < 0 || n.pitch > 127 || !finite(n.start) || n.start < 0 || !finite(n.duration) || n.duration <= 0 || n.start + n.duration > MAX_TIME) fail('A note has an invalid pitch or timing.');
    if (n.velocity !== undefined && (!finite(n.velocity) || n.velocity < 0 || n.velocity > 1)) fail('Invalid note velocity.');
    if (n.confidence !== undefined && (!finite(n.confidence) || n.confidence < 0 || n.confidence > 1)) fail('Invalid note confidence.');
    if (n.reviewed !== undefined && typeof n.reviewed !== 'boolean') fail('Invalid note review state.');
    if (n.track !== undefined && (!Number.isInteger(n.track) || n.track < 0 || n.track > 255)) fail('Invalid note track.');
    const id = n.id ?? `note-${index + 1}`;
    if (typeof id !== 'string' || !id || id.length > 120 || ids.has(id)) fail('Invalid or duplicate note ID.');
    ids.add(id);
    const result = { id, pitch: n.pitch, start: n.start, duration: n.duration, velocity: n.velocity ?? .8, track: n.track ?? 0,
      confidence: n.confidence ?? input.source.confidence ?? 1, reviewed: n.reviewed ?? input.source.reviewed ?? !['audio', 'visual'].includes(input.source.kind) };
    for (const key of ['measure', 'beat', 'staff', 'octave', 'alter', 'channel', 'durationBeats', 'dots']) {
      if (n[key] !== undefined) {
        if (!finite(n[key]) || Math.abs(n[key]) > 10000000) fail(`Invalid note ${key}.`);
        result[key] = n[key];
      }
    }
    for (const key of ['step', 'type', 'voice', 'accidental']) {
      if (n[key] !== undefined) {
        if (typeof n[key] !== 'string' || n[key].length > 80) fail(`Invalid note ${key}.`);
        result[key] = n[key];
      }
    }
    return result;
  }).sort((a, b) => a.start - b.start || a.pitch - b.pitch);
  const end = notes.reduce((value, n) => Math.max(value, n.start + n.duration), 0);
  if (input.duration !== undefined && (!finite(input.duration) || input.duration < end - .001 || input.duration > MAX_TIME)) fail('Invalid music duration.');
  const anchors = input.anchors ?? [{ videoTime: 0, musicTime: 0 }];
  if (!Array.isArray(anchors) || anchors.length > 1000) fail('Invalid alignment anchors.');
  let previousVideo = -1, previousMusic = -1;
  const normalizedAnchors = anchors.map(anchor => {
    if (!anchor || !finite(anchor.videoTime) || !finite(anchor.musicTime) || anchor.videoTime < 0 || anchor.musicTime < 0 || anchor.videoTime > MAX_TIME || anchor.musicTime > MAX_TIME || anchor.videoTime <= previousVideo || anchor.musicTime <= previousMusic) fail('Alignment anchors must increase in both video and music time.');
    previousVideo = anchor.videoTime; previousMusic = anchor.musicTime;
    return { videoTime: anchor.videoTime, musicTime: anchor.musicTime };
  });
  const normalizedViews = views();
  for (const key of Object.keys(normalizedViews)) {
    if (input.views?.[key] !== undefined && typeof input.views[key] !== 'boolean') fail('Invalid music view settings.');
    normalizedViews[key] = input.views?.[key] ?? true;
  }
  const tracks = input.tracks ?? [...new Set(notes.map(n => n.track))].map(id => ({ id, name: `Track ${id + 1}` }));
  if (!Array.isArray(tracks) || tracks.length > 256) fail('Invalid music tracks.');
  const trackIds = new Set();
  for (const t of tracks) {
    if (!t || !Number.isInteger(t.id) || t.id < 0 || t.id > 255 || trackIds.has(t.id) || typeof t.name !== 'string' || t.name.length > 500) fail('Invalid music track.');
    trackIds.add(t.id);
  }
  if (notes.some(n => !trackIds.has(n.track))) fail('A note references a missing track.');
  const tempos = input.tempos ?? [{ time: 0, bpm: 120 }];
  if (!Array.isArray(tempos) || tempos.length > 10000) fail('Invalid tempo map.');
  let previousTempo = -1;
  for (const t of tempos) {
    if (!t || !finite(t.time) || t.time < 0 || t.time < previousTempo || t.time > MAX_TIME || !finite(t.bpm) || t.bpm <= 0 || t.bpm > 10000) fail('Invalid music tempo.');
    previousTempo = t.time;
  }
  const id = input.id ?? uid();
  const measures = input.measures ?? [];
  if (!Array.isArray(measures) || measures.length > 30000) fail('Invalid music measures.');
  let previousMeasureEnd = 0;
  const normalizedMeasures = measures.map(m => {
    if (!m || !finite(m.start) || !finite(m.duration) || m.start < previousMeasureEnd - .001 || m.duration <= 0 || m.start + m.duration > MAX_TIME || !finite(m.number)) fail('Invalid music measure.');
    for (const key of ['numerator', 'denominator']) if (m[key] !== undefined && (!Number.isInteger(m[key]) || m[key] < 1 || m[key] > 128)) fail('Invalid measure time signature.');
    previousMeasureEnd = m.start + m.duration;
    return { start: m.start, duration: m.duration, number: m.number, ...(m.numerator ? { numerator: m.numerator, denominator: m.denominator } : {}) };
  });
  if (typeof id !== 'string' || !id || id.length > 120) fail('Invalid music ID.');
  if (input.title !== undefined && (typeof input.title !== 'string' || input.title.length > 500)) fail('Invalid music title.');
  if (input.source.confidence !== undefined && (!finite(input.source.confidence) || input.source.confidence < 0 || input.source.confidence > 1)) fail('Invalid source confidence.');
  if (input.source.reviewed !== undefined && typeof input.source.reviewed !== 'boolean') fail('Invalid source review state.');
  if (input.source.method !== undefined && (typeof input.source.method !== 'string' || input.source.method.length > 500)) fail('Invalid source method.');
  const extraction = input.extraction === undefined ? undefined : metadataCopy(input.extraction);
  if (extraction !== undefined && (!extraction || Array.isArray(extraction) || typeof extraction !== 'object')) fail('Invalid extraction metadata.');
  for (const warnings of [input.warnings, extraction?.warnings]) if (warnings !== undefined && (!Array.isArray(warnings) || warnings.length > 100 || warnings.some(w => typeof w !== 'string' || w.length > 500))) fail('Invalid music warnings.');
  const warnings = [...new Set([...(input.warnings ?? []), ...(extraction?.warnings ?? [])])];
  if (warnings.length > 100) fail('Invalid music warnings.');
  return { version: 1, id, title: input.title ?? input.source.name.replace(/\.[^.]+$/, ''),
    source: { name: input.source.name, kind: input.source.kind, confidence: input.source.confidence ?? 1, reviewed: input.source.reviewed ?? !['audio', 'visual'].includes(input.source.kind), ...(input.source.method ? { method: input.source.method } : {}) },
    notes, tracks: tracks.map(t => ({ id: t.id, name: t.name })), tempos: tempos.map(t => ({ time: t.time, bpm: t.bpm })), measures: normalizedMeasures,
    duration: input.duration ?? end, anchors: normalizedAnchors, views: normalizedViews,
    needsReview: notes.some(n => !n.reviewed), warnings, ...(extraction ? { extraction } : {}),
  };
}

function mapTime(anchors, time, from, to) {
  if (!finite(time)) return 0;
  if (!anchors?.length) return time;
  if (anchors.length === 1) return time - anchors[0][from] + anchors[0][to];
  let index = 0;
  while (index < anchors.length - 2 && time > anchors[index + 1][from]) index++;
  const a = anchors[index], b = anchors[index + 1];
  return a[to] + (time - a[from]) * (b[to] - a[to]) / (b[from] - a[from]);
}
export const videoToMusicTime = (music, time) => mapTime(music?.anchors, time, 'videoTime', 'musicTime');
export const musicToVideoTime = (music, time) => mapTime(music?.anchors, time, 'musicTime', 'videoTime');
export function addAlignmentAnchor(music, videoTime, musicTime) {
  const anchors = [...(music.anchors ?? []).filter(a => a.videoTime !== videoTime), { videoTime, musicTime }].sort((a, b) => a.videoTime - b.videoTime);
  return validateMusic({ ...music, anchors });
}
export function activeNotes(music, videoTime) {
  const time = videoToMusicTime(music, videoTime);
  return (music?.notes ?? []).filter(n => n.start <= time && n.start + n.duration > time);
}
const PITCH_NAMES = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];
export const noteName = pitch => `${PITCH_NAMES[((Math.round(pitch) % 12) + 12) % 12]}${Math.floor(pitch / 12) - 1}`;
export function chordLabel(notes) {
  const pitches = [...new Set(notes.map(n => (typeof n === 'number' ? n : n.pitch) % 12))];
  if (!pitches.length) return '—';
  const patterns = [[ '', [0, 4, 7]], ['m', [0, 3, 7]], ['dim', [0, 3, 6]], ['aug', [0, 4, 8]], ['sus2', [0, 2, 7]], ['sus4', [0, 5, 7]], ['7', [0, 4, 7, 10]], ['maj7', [0, 4, 7, 11]], ['m7', [0, 3, 7, 10]], ['m7♭5', [0, 3, 6, 10]], ['dim7', [0, 3, 6, 9]]];
  for (const [suffix, intervals] of patterns) for (const root of pitches) {
    if (pitches.length === intervals.length && intervals.every(i => pitches.includes((root + i) % 12))) return `${PITCH_NAMES[root]}${suffix}`;
  }
  return pitches.map(p => PITCH_NAMES[p]).join(' · ');
}

export function createManifest(project) {
  const result = { format: 'musictutor-project', version: project.schemaVersion === 2 ? 2 : 1, exportedAt: new Date().toISOString(), project: structuredClone(project) };
  validateManifest(result);
  return result;
}
export function validateManifest(data) {
  if (!data || data.format !== 'musictutor-project' || ![1,2].includes(data.version) || !data.project) fail('This is not a PianoTutor project manifest.');
  const [project] = validateBackup({ format: 'musictutor', version: data.version, projects: [data.project] });
  return project;
}

/** Standard MIDI files, formats 0 and 1, PPQN and SMPTE division. */
export function parseMidi(input, name = 'Performance.mid') {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (bytes.length > MAX_BYTES) fail('Music files must be smaller than 32 MB.');
  const data = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let pos = 0;
  const require = count => { if (pos + count > bytes.length) fail('The MIDI file is truncated.'); };
  const word = () => { require(2); const n = data.getUint16(pos); pos += 2; return n; };
  const long = () => { require(4); const n = data.getUint32(pos); pos += 4; return n; };
  const text = count => { require(count); const result = new TextDecoder().decode(bytes.subarray(pos, pos + count)); pos += count; return result; };
  if (text(4) !== 'MThd') fail('This is not a Standard MIDI file.');
  const headerSize = long();
  if (headerSize < 6 || headerSize > 1024) fail('Invalid MIDI header.');
  const format = word(), trackCount = word(), division = word();
  if (format > 1) fail('MIDI format 2 contains independent sequences; export a format 0 or 1 MIDI file.');
  if (!trackCount || trackCount > 256 || (format === 0 && trackCount !== 1) || division === 0) fail('Invalid MIDI track count or time division.');
  require(headerSize - 6); pos += headerSize - 6;
  const events = [], tracks = [], tempos = [{ tick: 0, tempo: 500000 }], meters = [{ tick: 0, numerator: 4, denominator: 4 }];
  let endTick = 0;
  for (let track = 0; track < trackCount; track++) {
    if (text(4) !== 'MTrk') fail('Missing MIDI track chunk.');
    const length = long(); require(length); const end = pos + length;
    let tick = 0, running = 0;
    const byte = () => { if (pos >= end) fail('The MIDI track is truncated.'); return bytes[pos++]; };
    const variable = () => { let value = 0; for (let i = 0; i < 4; i++) { const b = byte(); value = value * 128 + (b & 127); if (!(b & 128)) return value; } fail('Invalid MIDI variable-length integer.'); };
    const trackInfo = { id: track, name: `Track ${track + 1}` }; tracks.push(trackInfo);
    while (pos < end) {
      tick += variable(); endTick = Math.max(endTick, tick);
      let status = byte();
      if (status < 128) { if (!running) fail('Invalid MIDI running status.'); pos--; status = running; }
      else if (status < 240) running = status;
      else running = 0;
      if (status === 255) {
        const type = byte(), count = variable(); if (pos + count > end) fail('Truncated MIDI meta event.');
        if (type === 3) trackInfo.name = new TextDecoder().decode(bytes.subarray(pos, pos + count)).slice(0, 500);
        if (type === 81 && count === 3) {
          const tempo = bytes[pos] * 65536 + bytes[pos + 1] * 256 + bytes[pos + 2];
          if (!tempo) fail('Invalid MIDI tempo.');
          tempos.push({ tick, tempo });
        }
        if (type === 88 && count >= 2 && bytes[pos] > 0 && bytes[pos] <= 128 && bytes[pos + 1] <= 7) meters.push({ tick, numerator: bytes[pos], denominator: 2 ** bytes[pos + 1] });
        pos += count;
        if (type === 47) { pos = end; break; }
      } else if (status === 240 || status === 247) {
        const count = variable(); if (pos + count > end) fail('Truncated MIDI system event.'); pos += count;
      } else if (status >= 128 && status < 240) {
        const type = status >> 4, channel = status & 15, a = byte(), b = type === 12 || type === 13 ? 0 : byte();
        if (a > 127 || b > 127) fail('Invalid MIDI data byte.');
        if ([8, 9, 11].includes(type)) events.push({ tick, type, channel, a, b, track });
      } else fail('Unsupported MIDI system event.');
      if (events.length > MAX_NOTES * 8 || tempos.length > 10000) fail('The MIDI file contains too many events.');
    }
  }
  const sortedTempos = tempos.sort((a, b) => a.tick - b.tick);
  const tempoMap = [];
  let lastTick = 0, lastTime = 0, lastTempo = 500000;
  let smpteTick = null;
  if (division & 32768) {
    const fps = 256 - (division >> 8), ticksPerFrame = division & 255;
    if (![24, 25, 29, 30].includes(fps) || !ticksPerFrame) fail('Invalid SMPTE MIDI division.');
    smpteTick = 1 / ((fps === 29 ? 30000 / 1001 : fps) * ticksPerFrame);
  }
  for (const item of sortedTempos) {
    const time = smpteTick === null ? lastTime + (item.tick - lastTick) * lastTempo / division / 1000000 : item.tick * smpteTick;
    const entry = { ...item, time };
    if (tempoMap.at(-1)?.tick === item.tick) tempoMap[tempoMap.length - 1] = entry; else tempoMap.push(entry);
    lastTick = item.tick; lastTime = time; lastTempo = item.tempo;
  }
  const seconds = tick => {
    if (smpteTick !== null) return tick * smpteTick;
    let lo = 0, hi = tempoMap.length - 1;
    while (lo < hi) { const mid = Math.ceil((lo + hi) / 2); if (tempoMap[mid].tick <= tick) lo = mid; else hi = mid - 1; }
    const entry = tempoMap[lo]; return entry.time + (tick - entry.tick) * entry.tempo / division / 1000000;
  };
  if (seconds(endTick) > MAX_TIME) fail('Music longer than eight hours is not supported.');
  const held = new Map(), released = Array.from({ length: 16 }, () => []), sustain = Array(16).fill(false), notes = [];
  const finish = (note, tick) => {
    const start = seconds(note.tick), duration = seconds(tick) - start;
    if (duration <= 0) return;
    notes.push({ id: `note-${notes.length + 1}`, pitch: note.a, start, duration, velocity: note.b / 127, track: note.track, channel: note.channel, confidence: 1, reviewed: true,
      ...(smpteTick === null ? { beat: note.tick / division, durationBeats: (tick - note.tick) / division } : {}) });
    if (notes.length > MAX_NOTES) fail('Music must contain at most 100,000 notes.');
  };
  const releasePedal = (channel, tick) => { for (const note of released[channel]) finish(note, tick); released[channel] = []; };
  for (const event of events.sort((a, b) => a.tick - b.tick)) {
    const { type, channel, a, b, tick } = event, key = `${channel}:${a}`;
    if (type === 9 && b > 0) { const queue = held.get(key) ?? []; queue.push(event); held.set(key, queue); }
    else if (type === 8 || (type === 9 && b === 0)) {
      const queue = held.get(key), note = queue?.shift();
      if (note) { if (sustain[channel]) released[channel].push(note); else finish(note, tick); }
      if (!queue?.length) held.delete(key);
    } else if (type === 11 && a === 64) {
      sustain[channel] = b >= 64; if (!sustain[channel]) releasePedal(channel, tick);
    } else if (type === 11 && [120, 121, 123].includes(a)) {
      if (a !== 123) { sustain[channel] = false; releasePedal(channel, tick); }
      if (a !== 121) for (const [heldKey, queue] of held) if (queue[0]?.channel === channel) {
        for (const note of queue) { if (sustain[channel]) released[channel].push(note); else finish(note, tick); }
        held.delete(heldKey);
      }
    }
  }
  for (const queue of held.values()) for (const note of queue) finish(note, endTick);
  for (let channel = 0; channel < 16; channel++) releasePedal(channel, endTick);
  const measures = [], sortedMeters = meters.sort((a, b) => a.tick - b.tick);
  if (smpteTick === null) {
    let tick = 0, meterIndex = 0;
    while (tick < endTick && measures.length < 30000) {
      while (meterIndex + 1 < sortedMeters.length && sortedMeters[meterIndex + 1].tick <= tick) meterIndex++;
      const meter = sortedMeters[meterIndex], next = Math.min(tick + division * meter.numerator * 4 / meter.denominator, sortedMeters[meterIndex + 1]?.tick ?? Infinity);
      measures.push({ start: seconds(tick), duration: seconds(next) - seconds(tick), number: measures.length + 1, numerator: meter.numerator, denominator: meter.denominator });
      tick = next;
    }
  }
  return baseMusic(name, 'midi', notes, tracks, tempoMap.map(t => ({ time: t.time, bpm: 60000000 / t.tempo })), [], measures);
}

// A bounded, passive XML reader: no DOM insertion, resource fetches, or entity expansion.
function readXML(source) {
  if (typeof source !== 'string' || source.length > MAX_BYTES) fail('MusicXML files must be smaller than 32 MB.');
  if (/<!ENTITY\b/i.test(source) || /<!DOCTYPE[^>]*\[/i.test(source)) fail('MusicXML entity declarations are not supported.');
  const decode = value => value.replace(/&([^;]+);/g, (_, entity) => {
    const basic = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
    if (basic[entity]) return basic[entity];
    if (/^#(?:x[\da-f]+|\d+)$/i.test(entity)) {
      const n = entity[1].toLowerCase() === 'x' ? parseInt(entity.slice(2), 16) : Number(entity.slice(1));
      if (n > 0 && n <= 0x10ffff && !(n >= 0xd800 && n <= 0xdfff)) return String.fromCodePoint(n);
    }
    fail('Unsupported XML entity.');
  });
  const root = { name: '#document', attributes: {}, children: [], text: '' }, stack = [root];
  const tokens = /<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<\?[\s\S]*?\?>|<!DOCTYPE[^>]*>|<\/?[A-Za-z_][^>]*>|[^<]+/g;
  let cursor = 0, count = 0, match;
  while ((match = tokens.exec(source))) {
    if (match.index !== cursor) fail('Malformed MusicXML markup.');
    cursor = tokens.lastIndex;
    const token = match[0], parent = stack.at(-1);
    if (token.startsWith('<!--') || token.startsWith('<?') || token.startsWith('<!DOCTYPE')) continue;
    if (token.startsWith('<![CDATA[')) { parent.text += token.slice(9, -3); continue; }
    if (!token.startsWith('<')) { parent.text += decode(token); continue; }
    if (token.startsWith('</')) {
      const name = token.slice(2, -1).trim();
      if (stack.length < 2 || name !== parent.qualifiedName) fail('Mismatched MusicXML element.');
      stack.pop(); continue;
    }
    const parsed = /^<([\w:.-]+)([\s\S]*?)(\/?)>$/.exec(token);
    if (!parsed) fail('Malformed MusicXML element.');
    const [, qualifiedName, rest, selfClosing] = parsed;
    const attributes = {}, attrPattern = /\s+([\w:.-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
    let attr, consumed = 0;
    while ((attr = attrPattern.exec(rest))) {
      if (rest.slice(consumed, attr.index).trim()) fail('Malformed MusicXML attribute.');
      if (Object.hasOwn(attributes, attr[1])) fail('Duplicate MusicXML attribute.');
      attributes[attr[1]] = decode(attr[2] ?? attr[3]); consumed = attrPattern.lastIndex;
    }
    if (rest.slice(consumed).trim()) fail('Malformed MusicXML attribute.');
    const node = { name: qualifiedName.split(':').at(-1), qualifiedName, attributes, children: [], text: '' };
    parent.children.push(node);
    if (++count > 1000000) fail('MusicXML contains too many elements.');
    if (!selfClosing) stack.push(node);
    if (stack.length > 100) fail('MusicXML is nested too deeply.');
  }
  if (cursor !== source.length || stack.length !== 1 || root.children.length !== 1 || root.text.trim()) fail('Incomplete MusicXML document.');
  return root.children[0];
}
const child = (node, name) => node?.children.find(c => c.name === name);
const children = (node, name) => node?.children.filter(c => c.name === name) ?? [];
const value = (node, name, fallback = '') => child(node, name)?.text.trim() ?? fallback;
const number = (node, name, fallback = 0) => { const text = value(node, name); const n = text === '' ? fallback : Number(text); if (!Number.isFinite(n)) fail(`Invalid MusicXML ${name}.`); return n; };
const descendants = (node, name) => node.children.flatMap(c => [...(c.name === name ? [c] : []), ...descendants(c, name)]);
const STEP = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const UNIT = { maxima: 32, long: 16, breve: 8, whole: 4, half: 2, quarter: 1, eighth: .5, '16th': .25, '32nd': .125, '64th': .0625, '128th': .03125 };

/** Reads score-partwise MusicXML, including voices, ties, repeats, endings and tempo changes. */
export function parseMusicXML(source, name = 'Score.musicxml') {
  const root = readXML(source);
  if (root.name !== 'score-partwise') fail('Export a partwise MusicXML score (.musicxml, .xml or .mxl).');
  const parts = children(root, 'part');
  if (!parts.length || parts.length > 256) fail('Invalid MusicXML parts.');
  const partNames = new Map(children(child(root, 'part-list'), 'score-part').map(p => [p.attributes.id, value(p, 'part-name', 'Piano')]));
  const warnings = new Set(), tracks = [], parsedParts = [];
  let parsedNoteCount = 0;
  for (let track = 0; track < parts.length; track++) {
    const part = parts[track]; tracks.push({ id: track, name: (partNames.get(part.attributes.id) ?? `Part ${track + 1}`).slice(0, 500) });
    let divisions = 1, expectedLength = 4, transpose = 0, numerator = 4, denominator = 4;
    const measures = [];
    for (const [measureIndex, measure] of children(part, 'measure').entries()) {
      if (measureIndex > 10000) fail('MusicXML contains too many measures.');
      let cursor = 0, maximum = 0, previousOnset = 0;
      const entry = { notes: [], tempos: [], length: 0, repeatStart: false, repeatEnd: false, repeatTimes: 2, ending: null, endingStop: false };
      for (const item of measure.children) {
        if (item.name === 'attributes') {
          if (child(item, 'divisions')) { divisions = number(item, 'divisions'); if (divisions <= 0) fail('MusicXML divisions must be positive.'); }
          const time = child(item, 'time');
          if (time && !child(time, 'senza-misura')) {
            const beats = value(time, 'beats').split('+').reduce((sum, b) => sum + Number(b), 0), unit = number(time, 'beat-type');
            if (!(beats > 0 && unit > 0)) fail('Invalid MusicXML time signature.');
            expectedLength = beats * 4 / unit;
            numerator = beats; denominator = unit;
          }
          const transposition = child(item, 'transpose');
          if (transposition) transpose = number(transposition, 'chromatic') + number(transposition, 'octave-change') * 12;
        } else if (item.name === 'backup' || item.name === 'forward') {
          const duration = number(item, 'duration') / divisions;
          if (duration < 0) fail('Invalid MusicXML voice duration.');
          cursor += duration * (item.name === 'backup' ? -1 : 1);
          if (cursor < -.00001) fail('A MusicXML voice moves before the measure start.');
          cursor = Math.max(0, cursor); maximum = Math.max(maximum, cursor);
        } else if (item.name === 'direction' || item.name === 'sound') {
          const sound = item.name === 'sound' ? item : child(item, 'sound');
          let bpm = sound?.attributes.tempo ? Number(sound.attributes.tempo) : null;
          const metronome = descendants(item, 'metronome')[0];
          if (!bpm && metronome) {
            const perMinute = Number(value(metronome, 'per-minute'));
            const unit = UNIT[value(metronome, 'beat-unit')] ?? 1;
            const dots = children(metronome, 'beat-unit-dot').length;
            bpm = perMinute * unit * (2 - 1 / 2 ** dots);
          }
          if (bpm !== null && bpm !== undefined) {
            if (!Number.isFinite(bpm) || bpm <= 0 || bpm > 10000) fail('Invalid MusicXML tempo.');
            entry.tempos.push({ beat: Math.max(0, cursor + number(item, 'offset') / divisions), bpm });
          }
          if (sound && ['dacapo', 'dalsegno', 'tocoda', 'fine'].some(k => sound.attributes[k])) warnings.add('D.C., D.S. and coda jumps are not expanded. Add alignment anchors or export a performed MIDI for these passages.');
        } else if (item.name === 'barline') {
          const repeat = child(item, 'repeat'), ending = child(item, 'ending');
          if (repeat?.attributes.direction === 'forward') entry.repeatStart = true;
          if (repeat?.attributes.direction === 'backward') {
            entry.repeatEnd = true; entry.repeatTimes = Number(repeat.attributes.times ?? 2);
            if (!Number.isInteger(entry.repeatTimes) || entry.repeatTimes < 1 || entry.repeatTimes > 8) fail('Repeat counts above eight are not supported.');
          }
          if (ending?.attributes.type === 'start') {
            entry.ending = ending.attributes.number?.split(/\s*,\s*/).map(Number);
            if (!entry.ending?.length || entry.ending.some(n => !Number.isInteger(n) || n < 1 || n > 8)) fail('Unsupported MusicXML ending number.');
          }
          if (ending && ['stop', 'discontinue'].includes(ending.attributes.type)) entry.endingStop = true;
        } else if (item.name === 'note') {
          if (child(item, 'grace')) { warnings.add('Grace notes and ornaments are not assigned playback time; use a performed MIDI when their timing matters.'); continue; }
          const duration = number(item, 'duration') / divisions;
          if (!(duration > 0)) fail('MusicXML notes must have positive duration.');
          const isChord = !!child(item, 'chord'), onset = isChord ? previousOnset : cursor;
          if (!isChord) { previousOnset = cursor; cursor += duration; }
          maximum = Math.max(maximum, onset + duration, cursor);
          const pitch = child(item, 'pitch');
          if (!pitch) continue;
          const step = value(pitch, 'step'), alter = number(pitch, 'alter'), octave = number(pitch, 'octave', 4), midi = (octave + 1) * 12 + (STEP[step] ?? NaN) + alter + transpose;
          if (!Number.isInteger(midi) || midi < 0 || midi > 127) fail('Microtonal or out-of-range MusicXML pitches cannot be shown on a piano keyboard.');
          const tieTypes = children(item, 'tie').map(t => t.attributes.type);
          const measureNumber = Number(measure.attributes.number);
          entry.notes.push({ pitch: midi, beat: onset, durationBeats: duration, velocity: .8, track, step, alter, octave,
            measure: Number.isFinite(measureNumber) ? measureNumber : measureIndex + 1,
            staff: number(item, 'staff', 1), voice: value(item, 'voice', '1'), type: value(item, 'type', 'quarter'),
            dots: children(item, 'dot').length, accidental: value(item, 'accidental'), tieStart: tieTypes.includes('start'), tieStop: tieTypes.includes('stop') });
          if (++parsedNoteCount > MAX_NOTES) fail('MusicXML contains too many notes.');
          if (descendants(item, 'ornaments').length) warnings.add('Ornaments are shown as their written main notes; import performed MIDI for exact performance timing.');
        }
      }
      entry.length = measure.attributes.implicit === 'yes' ? maximum : Math.max(maximum, expectedLength);
      entry.numerator = numerator; entry.denominator = denominator;
      entry.number = Number.isFinite(Number(measure.attributes.number)) ? Number(measure.attributes.number) : measureIndex + 1;
      if (entry.length > 10000) fail('Invalid MusicXML measure duration.');
      measures.push(entry);
    }
    parsedParts.push(measures);
  }
  const count = Math.max(...parsedParts.map(p => p.length));
  if (!count) fail('MusicXML contains no measures.');
  // Expand the score's repeat roadmap once, then apply it to every part.
  const roadmap = [], repeatPass = new Map();
  let measureIndex = 0, repeatStart = 0, pass = 1, ending = null, previousRepeatEnd = -1;
  while (measureIndex < count) {
    if (roadmap.length > 30000) fail('MusicXML repeat roadmap is too long.');
    const measure = parsedParts[0][measureIndex] ?? { length: 0 };
    if (measure.repeatStart) { repeatStart = measureIndex; pass = repeatPass.get(measureIndex) ?? 1; }
    if (measure.ending) ending = measure.ending;
    const included = !ending || ending.includes(pass);
    if (included) roadmap.push(measureIndex);
    const endingStops = measure.endingStop;
    if (measure.repeatEnd && measureIndex !== previousRepeatEnd) {
      const times = measure.repeatTimes ?? 2;
      if (pass < times) {
        pass++; repeatPass.set(repeatStart, pass); measureIndex = repeatStart; ending = null; continue;
      }
      previousRepeatEnd = measureIndex;
    }
    if (endingStops) ending = null;
    measureIndex++;
  }
  const raw = [], rawTempos = [{ beat: 0, bpm: 120 }], tied = new Map(), rawMeasures = [];
  let beat = 0;
  for (const index of roadmap) {
    const length = Math.max(...parsedParts.map(p => p[index]?.length ?? 0));
    const measure = parsedParts[0][index];
    if (length > 0) rawMeasures.push({ beat, length, number: measure?.number ?? index + 1, numerator: measure?.numerator ?? 4, denominator: measure?.denominator ?? 4 });
    for (let partIndex = 0; partIndex < parsedParts.length; partIndex++) {
      const measure = parsedParts[partIndex][index]; if (!measure) continue;
      // Other parts can carry the only tempo direction in an otherwise shared score.
      for (const tempo of measure.tempos) rawTempos.push({ beat: beat + tempo.beat, bpm: tempo.bpm });
      for (const n of measure.notes) {
        const note = { ...n, beat: beat + n.beat }, tieKey = `${n.track}:${n.staff}:${n.voice}:${n.pitch}`, prior = tied.get(tieKey);
        if (n.tieStop && prior && Math.abs(prior.beat + prior.durationBeats - note.beat) < .00001) {
          prior.durationBeats += n.durationBeats;
          if (!n.tieStart) tied.delete(tieKey);
        } else {
          raw.push(note);
          if (n.tieStart) tied.set(tieKey, note); else tied.delete(tieKey);
        }
        if (raw.length > MAX_NOTES) fail('Expanded MusicXML contains too many notes.');
      }
    }
    beat += length;
  }
  const tempoMap = [];
  for (const tempo of rawTempos.sort((a, b) => a.beat - b.beat)) {
    const previous = tempoMap.at(-1);
    const time = previous ? previous.time + (tempo.beat - previous.beat) * 60 / previous.bpm : 0;
    if (previous?.beat === tempo.beat) tempoMap[tempoMap.length - 1] = { ...tempo, time }; else tempoMap.push({ ...tempo, time });
  }
  const seconds = position => {
    let lo = 0, hi = tempoMap.length - 1;
    while (lo < hi) { const mid = Math.ceil((lo + hi) / 2); if (tempoMap[mid].beat <= position) lo = mid; else hi = mid - 1; }
    const t = tempoMap[lo]; return t.time + (position - t.beat) * 60 / t.bpm;
  };
  const notes = raw.map((n, i) => ({ ...n, id: `note-${i + 1}`, start: seconds(n.beat), duration: seconds(n.beat + n.durationBeats) - seconds(n.beat), confidence: 1, reviewed: true }));
  const measures = rawMeasures.map(m => ({ start: seconds(m.beat), duration: seconds(m.beat + m.length) - seconds(m.beat), number: m.number, numerator: m.numerator, denominator: m.denominator }));
  const result = baseMusic(name, 'musicxml', notes, tracks, tempoMap.map(t => ({ time: t.time, bpm: t.bpm })), [...warnings], measures);
  const title = value(child(root, 'work'), 'work-title') || value(root, 'movement-title');
  if (title) result.title = title.slice(0, 500);
  return result;
}

function readCompressedXML(bytes) {
  // Inspect the ZIP central directory before decompression to bound memory use.
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let end = -1;
  for (let at = bytes.length - 22; at >= Math.max(0, bytes.length - 65557); at--) if (view.getUint32(at, true) === 0x06054b50) { end = at; break; }
  if (end < 0 || view.getUint16(end + 4, true) || view.getUint16(end + 6, true)) fail('Invalid compressed MusicXML archive.');
  const count = view.getUint16(end + 10, true), directoryLength = view.getUint32(end + 12, true), directoryOffset = view.getUint32(end + 16, true);
  if (!count || count > 256 || view.getUint16(end + 8, true) !== count || directoryOffset + directoryLength > end) fail('Compressed MusicXML has too many files or an invalid directory.');
  let pos = directoryOffset, total = 0;
  for (let i = 0; i < count; i++) {
    if (pos + 46 > end || view.getUint32(pos, true) !== 0x02014b50) fail('Invalid MusicXML archive entry.');
    const flags = view.getUint16(pos + 8, true), method = view.getUint16(pos + 10, true), size = view.getUint32(pos + 24, true);
    if (flags & 1 || ![0, 8].includes(method) || size > MAX_BYTES || (total += size) > MAX_BYTES * 2) fail('MusicXML archive is encrypted, too large, or uses unsupported compression.');
    pos += 46 + view.getUint16(pos + 28, true) + view.getUint16(pos + 30, true) + view.getUint16(pos + 32, true);
    if (pos > end) fail('Truncated MusicXML archive directory.');
  }
  if (pos !== directoryOffset + directoryLength) fail('Invalid MusicXML archive directory length.');
  let files;
  try { files = unzipSync(bytes, { filter: file => file.originalSize <= MAX_BYTES }); }
  catch { fail('The compressed MusicXML archive could not be read.'); }
  const containerBytes = files['META-INF/container.xml'];
  if (!containerBytes) fail('The MusicXML archive is missing META-INF/container.xml.');
  const container = readXML(new TextDecoder().decode(containerBytes));
  const rootfiles = descendants(container, 'rootfile');
  const rootfile = rootfiles.find(f => f.attributes['media-type'] === 'application/vnd.recordare.musicxml+xml') ?? rootfiles[0];
  const path = rootfile?.attributes['full-path'];
  if (!path || path.startsWith('/') || path.split('/').includes('..') || !Object.hasOwn(files, path)) fail('The MusicXML archive does not contain its declared score.');
  return new TextDecoder().decode(files[path]);
}

export async function importMusicFile(file) {
  if (!file || typeof file.arrayBuffer !== 'function') fail('Choose a MIDI, MusicXML or extracted-note JSON file.');
  if (file.size > MAX_BYTES) fail('Music files must be smaller than 32 MB.');
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes.length > MAX_BYTES) fail('Music files must be smaller than 32 MB.');
  const name = String(file.name || 'Music').slice(0, 500);
  if (bytes[0] === 77 && bytes[1] === 84 && bytes[2] === 104 && bytes[3] === 100) return parseMidi(bytes, name);
  if (bytes[0] === 80 && bytes[1] === 75) return parseMusicXML(readCompressedXML(bytes), name);
  const source = new TextDecoder().decode(bytes).replace(/^\uFEFF/, '').trim();
  if (source.startsWith('<')) return parseMusicXML(source, name);
  if (source.startsWith('{')) {
    let data;
    try { data = JSON.parse(source); } catch { fail('The music JSON file is not valid JSON.'); }
    return validateMusic(data);
  }
  fail('Choose a MIDI (.mid), MusicXML (.musicxml, .xml, .mxl), or extracted-note JSON file.');
}
